/**
 * MuraTile — shared Mura (variance) indicator tile for Scorecard page.
 *
 * Extracted from ScorecardPage.tsx in BD-1 D.3 (CLEAN-J6-A3).
 * Renders a Mura signal (FTT Mura, Cycle-Time Mura) with mean/stddev/CoV
 * and an optional sparkline. Uses the same visual design as before.
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { t } from '@/lib/i18n';
import type { RorosMuraIndicatorDTO } from '@/services/roros/control-tower.service';

const VARIANT_MAP = {
  ok: 'success',
  warning: 'warning',
  critical: 'error',
  informational: 'default',
} as const;

export interface MuraTileProps {
  label: string;
  mura: RorosMuraIndicatorDTO | null;
}

export function MuraTile({ label, mura }: MuraTileProps) {
  if (!mura) {
    return (
      <Card padding="md" className="space-y-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{t('Insufficient data for Mura calculation.')}</p>
      </Card>
    );
  }

  const variant = VARIANT_MAP[mura.status] ?? 'default';

  return (
    <Card
      padding="md"
      className="space-y-2"
      data-testid={`mura-tile-${mura.signal.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <Badge variant={variant}>{t(mura.status)}</Badge>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <p className="text-muted-foreground">{t('Mean')}</p>
          <p className="font-semibold text-foreground">{mura.mean.toFixed(1)}</p>
        </div>
        <div>
          <p className="text-muted-foreground">{t('Std Dev')}</p>
          <p className="font-semibold text-foreground">{mura.stddev.toFixed(1)}</p>
        </div>
        <div>
          <p className="text-muted-foreground">{t('CoV')}</p>
          <p className="font-semibold text-foreground">{mura.coefficientOfVariation.toFixed(2)}</p>
        </div>
      </div>
      {mura.weeklyValues.length > 0 && (
        <ResponsiveContainer width="100%" height={60}>
          <LineChart data={mura.weeklyValues.map((v, i) => ({ week: i + 1, value: v }))}>
            <Line type="monotone" dataKey="value" stroke="#0ea5e9" dot={false} strokeWidth={2} />
            <XAxis dataKey="week" hide />
            <YAxis hide />
            <Tooltip formatter={(v) => Number(v).toFixed(1)} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}
